import { useState, useRef, useEffect } from 'react';
import styles from './MultiSelectFilter.module.css';

interface Option {
  key: string;
  label: string;
}

interface Props {
  label: string;
  options: Option[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
  /** Visual variant: dark = on a dark header (default), light = on a light surface */
  variant?: 'dark' | 'light';
}

export function MultiSelectFilter({ label, options, selected, onChange, variant = 'dark' }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const allSelected = selected.size === 0;
  const displayText = allSelected
    ? `${label}: All`
    : `${label}: ${selected.size} selected`;

  function toggle(key: string) {
    const next = new Set(selected);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onChange(next);
  }

  function clear() {
    onChange(new Set());
  }

  const rootClass = `${styles.root} ${variant === 'light' ? styles.light : styles.dark}`;

  return (
    <div className={rootClass} ref={ref}>
      <button
        className={`${styles.trigger} ${!allSelected ? styles.triggerActive : ''}`}
        onClick={() => setOpen(!open)}
      >
        {displayText}
        <span className={styles.chevron}>{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className={styles.dropdown}>
          {!allSelected && (
            <button className={styles.clearBtn} onClick={clear}>
              Clear
            </button>
          )}
          {options.map(({ key, label: optLabel }) => (
            <label key={key} className={styles.option}>
              <input
                type="checkbox"
                checked={selected.has(key)}
                onChange={() => toggle(key)}
                className={styles.checkbox}
              />
              {optLabel}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
